import { Group, type GroupProps, Text } from "@mantine/core"
import { IconChevronDown, IconSelector } from "@tabler/icons-react"
import type { PropsWithChildren } from "react"
import { useQueryState } from "@/hooks"

interface IProps {
  field: string
  defaultActive?: boolean
  defaultSort?: "asc" | "desc"
}

export function SortHeader({
  field,
  defaultActive,
  defaultSort = "desc",
  ...props
}: PropsWithChildren<IProps & GroupProps>) {
  const [sort, setSort] = useQueryState("sort")
  const active = sort === `${field}` || sort === `-${field}` || (defaultActive && !sort)

  // Effective direction while the column is active.
  const direction = sort === `${field}` || (defaultActive && !sort && defaultSort === "asc") ? "asc" : "desc"

  return (
    <SortHeaderBox
      active={active}
      setActive={(sort) => setSort(sort === "asc" ? field : `-${field}`)}
      sort={active ? direction : defaultSort === "asc" ? "desc" : "asc"}
      {...props}
    />
  )
}

interface IBoxProps {
  active?: boolean
  setActive?: (sort: "asc" | "desc") => void
  sort?: "asc" | "desc"
}

export function SortHeaderBox({
  active,
  setActive,
  sort,
  children,
  ...props
}: PropsWithChildren<IBoxProps & GroupProps>) {
  return (
    <Group
      gap={4}
      wrap="nowrap"
      className="pointer"
      {...props}
      onClick={() => setActive?.(sort === "asc" ? "desc" : "asc")}
    >
      {active ? (
        <IconChevronDown
          size={16}
          stroke="3"
          style={{
            transform: sort === "asc" ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease-in-out",
          }}
        />
      ) : (
        <IconSelector size={16} />
      )}
      <Text fw={active ? 700 : 500} truncate>
        {children}
      </Text>
    </Group>
  )
}
